import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { baseOption, axeCategorie, axeValeur, TAILLE_TEXTE, COULEUR_TEXTE } from './echartsTheme';
import { libelleHorizon, cleCouleurHorizon } from '../utils/horizon';
import { YEAR_COLORS } from '../constants';

const NIVEAUX = ['Très courant', 'Courant', 'Exceptionnel', 'Catastrophique'];

/**
 * Pertes par niveau de risque, une courbe par horizon (Impacts globaux).
 *
 * Remplace GraphDifference sur cette page : GraphDifference ne sait comparer
 * que deux horizons (référence + horizon choisi) et reste utilisé tel quel
 * par les exports PDF. Ici on trace les trois horizons d'un coup, chacun dans
 * sa couleur YEAR_COLORS.
 *
 * @param scenario  clé de scénario ('2degre', '4degre', 'hybride', 'tracc')
 * @param risques   risques[horizon] -> tableau des niveaux (seuls les 4 premiers sont tracés)
 * @param horizons  les horizons à tracer, dans l'ordre (référence incluse)
 */
export default function RisquesNiveauxChart({ scenario, risques, horizons }) {
  const disponibles = horizons.filter((h) => Array.isArray(risques?.[h]));
  if (disponibles.length === 0) {
    return <p className="tdb-chargement">Pertes indisponibles pour ce scénario.</p>;
  }

  const series = disponibles.map((horizon) => {
    const couleur = YEAR_COLORS[cleCouleurHorizon(scenario, horizon)] ?? '#888888';
    return {
      name: libelleHorizon(scenario, horizon),
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 10,
      lineStyle: { width: 3, color: couleur },
      itemStyle: { color: couleur },
      // Au survol de la courbe OU de son horizon dans la légende : on isole la
      // série et on affiche ses valeurs au-dessus des points (même principe
      // que graphDifference.js).
      emphasis: {
        focus: 'series',
        label: {
          show: true,
          position: 'top',
          fontSize: 14,
          formatter: (param) => `${Number(param.data).toFixed(2)} %`,
        },
      },
      data: risques[horizon].slice(0, NIVEAUX.length),
    };
  });

  const option = {
    ...baseOption(),
    grid: { left: 12, right: 24, bottom: 48, top: 72, containLabel: true },
    legend: {
      top: 0,
      data: series.map((s) => s.name),
      textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      itemGap: 18,
    },
    // Pas de tooltip : les valeurs s'affichent au-dessus des points au survol
    // (emphasis.label). À désactiver explicitement : baseOption() en définit un.
    tooltip: { show: false },
    xAxis: axeCategorie({ data: NIVEAUX, nom: 'Pertes' }),
    yAxis: axeValeur({ nom: 'Pertes en %', formatteur: (v) => `${v} %` }),
    series,
  };

  return <ResponsiveECharts option={option} style={{ height: 450 }} />;
}
